import { z } from 'zod';
import { uuidSchema } from './validation.js';
/**
 * Just-in-time purchase orders and To buy (BLU-1268, plan 90 section 5).
 *
 * A just-in-time (blank, location) pair holds no tracked stock. Creating a
 * batch writes its shopping list: one just-in-time PO per supplier
 * (`purchase_orders.kind = 'just_in_time'`, linked to the batch), owned by the
 * floor OWNER org. Staff buy, edit the PO to what they paid and press Complete
 * (or Bought as listed), which records cost only.
 *
 * Inputs only: the organisation and the user always come from auth, so no
 * schema here carries an organizationId (zod's default strip drops one).
 */
export const PURCHASE_ORDER_KINDS = ['standard', 'just_in_time', 'order_and_wait'];
export const purchaseOrderKindSchema = z.enum(PURCHASE_ORDER_KINDS);
/**
 * The kinds that are a buy list, not an inbound delivery: a just-in-time PO
 * (a batch's shopping list) and an order-and-wait PO (blanks bought ahead for
 * waiting orders). Neither is received into stock, shipped, deposit-billed or
 * worked by a supplier partner; both complete for cost only. Every guard that
 * used to read `kind === 'just_in_time'` asks this instead.
 */
export const BUY_LIST_PO_KINDS = ['just_in_time', 'order_and_wait'];
export function isBuyListPoKind(kind) {
    return !!kind && BUY_LIST_PO_KINDS.includes(kind);
}
/**
 * The refusal keys of the just-in-time PO paths, the one list the backend
 * (`lib/jit-po-errors.ts`) and the browser both read. Each rides as
 * `JIT_PO_<KEY>` in the backend's `FORWARDABLE_CAUSE_CODES` (`trpc.ts`) and on
 * a `JIT_PO_<KEY>:<json>` message marker (tRPC 11.7.1 drops wrapped causes on
 * the wire); a backend test pins every key into the allowlist.
 *
 *   NOT_FOUND          no just-in-time PO with that id in the caller's org
 *   NO_LINES           Complete on a PO with no line (cancel it instead)
 *   NOT_OPEN           Complete on a cancelled PO
 *   NO_SUPPLIER        Bought as listed / Xero push on a "No supplier" PO
 *   EDITED             Bought as listed after a line was changed (open it and Complete)
 *   OPEN_EXISTS        the batch already has an open just-in-time PO for that supplier
 *   NOT_RECEIVABLE     receive / re-edit / revert / cost rebase / shipped quantities on a just-in-time PO
 *   STATUS_REFUSED     a status other than Cancelled, or the short-close, on a just-in-time PO
 *   DELETE_REFUSED     delete on a just-in-time PO (cancel keeps its batch links)
 *   LINE_NOT_JUST_IN_TIME  a line added to a just-in-time PO for a blank that is not just in time here
 *   DEV_LINE_REFUSED   a Product Dev line aimed at a just-in-time PO
 *   SHIPMENT_REFUSED   linking a just-in-time PO to a shipment
 *   XERO_NOT_COMPLETE  Xero push of a just-in-time PO before Complete
 *   XERO_ACCOUNT_UNSET Xero push before the org set its just-in-time purchases account
 *   XERO_DEPOSIT_REFUSED a deposit bill or a separate invoice bill for a just-in-time PO
 *   BLANK_JUST_IN_TIME a standard receive or a transfer line on a just-in-time blank
 *   BATCH_NOT_FOUND    sync / buy list for a batch the caller cannot see
 */
export const JIT_PO_ERROR_KEYS = [
    'NOT_FOUND',
    'NO_LINES',
    'NOT_OPEN',
    'NO_SUPPLIER',
    'EDITED',
    'OPEN_EXISTS',
    'NOT_RECEIVABLE',
    'STATUS_REFUSED',
    'DELETE_REFUSED',
    'LINE_NOT_JUST_IN_TIME',
    'DEV_LINE_REFUSED',
    'SHIPMENT_REFUSED',
    'XERO_NOT_COMPLETE',
    'XERO_ACCOUNT_UNSET',
    'XERO_DEPOSIT_REFUSED',
    'BLANK_JUST_IN_TIME',
    'BATCH_NOT_FOUND',
];
// ── Batch buy list (Production App pick list) ─────────────────────────────────
export const getBuyListSchema = z.object({ batchId: uuidSchema });
/** "Update buy list": re-runs the batch's sync (same RPC createBatch calls). */
export const syncBuyListSchema = z.object({ batchId: uuidSchema });
// ── The JIT PO page ───────────────────────────────────────────────────────────
export const getJitOutcomesSchema = z.object({ orderId: uuidSchema });
/**
 * Complete (cost only). `asListed: true` is "Bought as listed": the backend
 * checks every line still reads bought = planned (and nothing was added or
 * removed) and that the PO has a supplier, then completes. A PO someone has
 * edited is refused with EDITED: open it and press Complete.
 */
export const completeJustInTimeSchema = z.object({
    orderId: uuidSchema,
    locationId: uuidSchema,
    asListed: z.boolean().optional(),
});
// ── Add a blank (the JIT PO page's picker) ────────────────────────────────────
export const JIT_BLANKS_DEFAULT_LIMIT = 50;
export const JIT_BLANKS_MAX_LIMIT = 100;
/** Ids sent as one PostgREST `not in` list; matches the backend's `.in()` chunk bound. */
export const JIT_BLANKS_MAX_EXCLUDED = 75;
export const JIT_BLANKS_MAX_SEARCH = 100;
/**
 * The blanks that are just in time at one location, for the "Add a blank"
 * picker: one page, narrowed by `search` (SKU or title) and by the lines the PO
 * already holds (`excludeSkuIds`), counted over that same narrowed set.
 */
export const listJitBlanksSchema = z.object({
    locationId: uuidSchema,
    search: z.string().trim().max(JIT_BLANKS_MAX_SEARCH).optional(),
    limit: z.number().int().min(1).max(JIT_BLANKS_MAX_LIMIT).default(JIT_BLANKS_DEFAULT_LIMIT),
    excludeSkuIds: z.array(uuidSchema).max(JIT_BLANKS_MAX_EXCLUDED).optional(),
});
export const jitBlankOptionSchema = z.object({
    skuId: z.string(),
    sku: z.string().nullable(),
    title: z.string().nullable(),
    colour: z.string().nullable(),
    size: z.string().nullable(),
});
export const listJitBlanksResultSchema = z.object({
    blanks: z.array(jitBlankOptionSchema),
    /** Every match, so "N more" is honest when the page is full. */
    total: z.number().int().min(0),
});
// ── To buy (stocked blanks below keep at least) ───────────────────────────────
export const TO_BUY_MAX_LINES = 500;
export const TO_BUY_MAX_QTY = 999_999;
export const getToBuySchema = z.object({ locationId: uuidSchema });
export const createDraftsFromToBuySchema = z.object({
    locationId: uuidSchema,
    lines: z
        .array(z.object({
        skuId: uuidSchema,
        qty: z.number().int().min(1).max(TO_BUY_MAX_QTY),
    }))
        .min(1)
        .max(TO_BUY_MAX_LINES),
});
